<script setup lang="ts">
import { isNull } from 'lodash'
import { ArrowDownCircleIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/20/solid'
import { useMutation } from '@vue/apollo-composable'
import type { User } from '@/components/users/interfaces.ts'
import {
  ProjectSettingsDeleteAssistantFile,
  ProjectSettingsDisableAssistantFile,
  ProjectSettingsEnableAssistantFile,
  UploadAiFilesMutation,
} from '@/components/projects/graphql/project.mutations.ts'
import type { ChatGptAssistantFile } from '@/components/projects/interfaces.ts'
import { ProjectAIType } from '@/enums.ts'
import { useToast } from 'vue-toastification'
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import prettyBytes from 'pretty-bytes'
import { computed, ref } from 'vue'
import { faCheckCircle, faCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { ASSISTANT_AI_FILES_PREFIX } from '@/config/config.ts'
import { API_BASE_URL } from '@/config/env.const.ts'
import { useTranslations } from '@/composables/useTranslations.ts'
import { GetProjectChatGPTModelsAndProjectFiles } from '@/components/projects/graphql/project.queries.ts'
import type { ApolloCache } from '@apollo/client/core'
import { getDefaultApolloClient } from '@/graphql.ts'

dayjs.extend(isToday)
dayjs.extend(customParseFormat)

// TODO: Limit filename length?

const isFileChangeRequestRunning = ref<boolean>(false)
const toast = useToast()
const props = defineProps<{
  projectId: number | null
  files: ChatGptAssistantFile[]
}>()
const files = computed<ChatGptAssistantFile[]>(() => {
  return props.files
})

const fileActionButtonClasses = computed<string>(() => {
  return 'h-9 w-9 inline-flex items-center justify-center gap-x-1.5 rounded-md focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 text-sm text-center font-light text-white shadow-xs hover:cursor-pointer'
})
const fileActionButtonClassesForInfobox = computed<string>(() => {
  return 'h-7 w-7 rounded-md focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 text-sm font-light text-white shadow-xs ml-1 mr-1'
})

// Mutations
const { t } = useTranslations()
const {
  onError: onFileEnableFailed,
  onDone: onFileEnableSucceed,
  mutate: enableAssistantFile
} = useMutation<{ file: ChatGptAssistantFile }>(ProjectSettingsEnableAssistantFile)
const {
  onError: onFileDisableFailed,
  onDone: onFileDisableSucceed,
  mutate: disableAssistantFile
} = useMutation<{ file: ChatGptAssistantFile }>(ProjectSettingsDisableAssistantFile)
const {
  onError: onFileDeletionFailed,
  onDone: onFileDeletionSucceed,
  mutate: deleteFile
} = useMutation<{ file: ChatGptAssistantFile }>(ProjectSettingsDeleteAssistantFile)
const {
  onError: onFileUploadingFailed,
  onDone: onFileUploadingDone,
  mutate: uploadFile,
} = useMutation<{ files: ChatGptAssistantFile[] }>(UploadAiFilesMutation)


// @ts-expect-error Jevgeni fix it
onFileDeletionSucceed(({ data: { file }}) => {
  toast.success(`File ${file.name} deleted`)
  setFileChangeRequestRunning(false)
})

onFileDeletionFailed((error) => {
  console.error('onFileInputChangedDoUpload | onError:', error)
  toast.error(error.message)
  setFileChangeRequestRunning(false)
})

// @ts-expect-error Jevgeni fix it
onFileEnableSucceed(({ data: { file }}) => {
  toast.success(`File «${file.name}» included`)
  setFileChangeRequestRunning(false)
})

onFileEnableFailed((error) => {
  console.error('onEnableFileButtonClicked | onError:', error)
  setFileChangeRequestRunning(false)
  toast.error(error.message)
})

// @ts-expect-error Jevgeni fix it
onFileDisableSucceed(({ data: { file }}) => {
  toast.warning(`«${file.name}» excluded`)
  setFileChangeRequestRunning(false)
})

onFileDisableFailed((error) => {
  toast.error(error.message)
  setFileChangeRequestRunning(false)
})

// @ts-expect-error Jevgeni fix it
onFileUploadingDone(({ data: { files }}) => {
  rewriteFilesInCache(files)
  toast.success('Upload succeed')
})

onFileUploadingFailed((error) => {
  console.error('onFileInputChangedDoUpload | onError:', error)
  toast.error(error.message)
})

function formatFileCreationDate(time: string): string {
  const instance = dayjs(time, 'YYYY-MM-DDTHH:mm:ss.000ZZ') // TODO: Config

  return instance.format('D MMM YYYY HH:mm')
}

function onAddFileButtonClicked(): void {
  document.getElementById('chatGptAssistantFiles')?.click()
}

function setFileChangeRequestRunning(flag: boolean): void {
  isFileChangeRequestRunning.value = flag
}

function updateFileStateInCache (
  proxy: ApolloCache,
  file: ChatGptAssistantFile,
  deleteFile = false
): void {
  const info = proxy.readQuery<{ files: ChatGptAssistantFile[] }>({
    query: GetProjectChatGPTModelsAndProjectFiles,
    variables: { projectId: props.projectId },
  })
  const files = (info?.files ?? []) as ChatGptAssistantFile[]

  proxy.writeQuery({
    query: GetProjectChatGPTModelsAndProjectFiles,
    variables: { projectId: props.projectId },
    data: {
      ...info,
      files: deleteFile
        ? files.filter(({ key }) => file.key !== key)
        : files.map((fileCached) => file.key === fileCached.key ? file : fileCached)
    },
  })
}

function rewriteFilesInCache (
  files: ChatGptAssistantFile[]
): void {
  const info = getDefaultApolloClient().readQuery<{ files: User[] }>({
    query: GetProjectChatGPTModelsAndProjectFiles,
    variables: { projectId: props.projectId },
  })

  getDefaultApolloClient().writeQuery({
    query: GetProjectChatGPTModelsAndProjectFiles,
    variables: { projectId: props.projectId },
    data: {...info, files },
  })
}

async function onEnableFileButtonClicked(event: Event, fileKey: string): Promise<void> {
  event.preventDefault()
  setFileChangeRequestRunning(true)
  await enableAssistantFile({
    variables: {
      projectId: props.projectId,
      aiType: ProjectAIType.ChatGPTResponsesAPI,
      fileKey,
    },
    // @ts-expect-error Jevgeni fix it
    update: (proxy, { data: { file } }) => updateFileStateInCache(proxy, file)
  })
}

function onDisableFileButtonClicked(event: Event, fileKey: string): void {
  event.preventDefault()
  setFileChangeRequestRunning(true)
  disableAssistantFile({
    variables: {
      projectId: props.projectId,
      aiType: ProjectAIType.ChatGPTResponsesAPI,
      fileKey
    },
    // @ts-expect-error Jevgeni fix it
    update: (proxy, { data: { file } }) => updateFileStateInCache(proxy, file)
  })
}

function onDeleteFileButtonClicked(event: Event, fileKey: string): void {
  event.preventDefault()
  setFileChangeRequestRunning(true)
  deleteFile({
    variables: {
      projectId: props.projectId,
      aiType: ProjectAIType.ChatGPTResponsesAPI,
      fileKey,
    },
    // @ts-expect-error Jevgeni fix it
    update: (proxy, { data: { file } }) => updateFileStateInCache(proxy, file, true)
  })
}

function onFileInputChangedDoUpload(event: Event): void {
  event.preventDefault() // Prevent whole form sending
  const input = event.target as HTMLInputElement

  uploadFile({
    variables: {
      projectId: props.projectId,
      aiType: ProjectAIType.ChatGPTResponsesAPI,
      req: input.files,
    },
  })
}
</script>
<template>
  <div class="px-4 sm:px-6 lg:px-8">

      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <td colspan="5" class="py-5 text-center">
                  <button
                    type="button"
                    @click="onAddFileButtonClicked"
                    class="inline-flex items-center gap-x-1.5 rounded-lg px-3 py-2 bg-indigo-500 text-xs font-semibold text-white shadow-xs hover:bg-indigo-400 hover:cursor-pointer focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <PlusCircleIcon class="-mr-0.5 size-5" aria-hidden="true" />
                    {{ `${t('common.buttons.add')} (.pdf, .txt)` }}
                  </button>
                  <input
                    multiple
                    id="chatGptAssistantFiles"
                    type="file"
                    class="hidden"
                    @change="onFileInputChangedDoUpload"
                  />
                </td>
              </tr>
              <tr class="text-center">
                <th scope="col" class="py-3.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                  {{ t('settings.fileTable.name') }}
                </th>
                <th scope="col" class="px-3 py-3.5 text-sm font-medium text-gray-900">
                  {{ t('settings.fileTable.size') }}
                </th>
                <th scope="col" class="px-3 py-3.5 text-sm font-medium text-gray-900">
                  {{ t('settings.fileTable.created') }}
                </th>
                <th scope="col" class="px-3 py-3.5 text-sm font-medium text-gray-900">
                  {{ t('settings.fileTable.updated') }}
                </th>
                <th scope="col" class="relative py-3.5 pl-3 text-sm font-medium pr-4 sm:pr-3">
                  {{ t('settings.fileTable.actions') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="5" class="py-5 text-center" v-if="files.length === 0">
                  <span class="text-sm">
                    {{ t('settings.fileTable.ifYouUploadFilesTheAssistantWillUseTheAnswersFromThem') }}
                  </span>
                  <br />
                  <span>
                    {{ t('settings.fileTable.theFilesYouUpload') }}
                    <span class="font-medium text-orange-600">
                      {{ t('settings.fileTable.shouldNotContainPeoplesPersonalOrContactData') }}
                    </span>
                  </span>
                </td>
              </tr>
              <tr v-for="file in files" :key="file.key" class="text-center">
                <td
                  class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                >
                  <span
                    class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                    >{{ file.name }}</span
                  >
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <span
                    class="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                  >
                    {{ prettyBytes(file?.sizeBytes ?? 0) }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <span
                    class="mt-2 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                  >
                    {{ formatFileCreationDate(file.createdBy!.date) }}
                  </span>
                  <br />
                  <span
                    class="mt-1 inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                  >
                    {{ file.createdBy!.firstname }} {{ file.createdBy!.lastname }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  <div v-if="isNull(file?.updatedBy)"></div>
                  <div v-else>
                    <span
                      class="inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                    >
                      {{ formatFileCreationDate(file?.updatedBy?.date ?? '') }}
                    </span>
                    <br />
                    <span
                      class="mt-1 inline-flex items-center rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-700/10"
                    >
                      {{ file.createdBy?.firstname }} {{ file.updatedBy?.lastname }}
                    </span>
                  </div>
                </td>
                <td class="py-4 pl-3 sm:py-2">
                  <div class="inline-flex items-center gap-3">
                    <a
                      target="_self"
                      :href="`${API_BASE_URL}${ASSISTANT_AI_FILES_PREFIX}${file.key}`"
                      :class="[fileActionButtonClasses, 'focus-visible:outline-indigo-600 bg-green-600 hover:bg-green-700']"
                    >
                      <ArrowDownCircleIcon class="size-6"/>
                    </a>
                    <button
                      v-if="file.options.chatGptIsIncluded"
                      type="button"
                      @click="(ev) => onDisableFileButtonClicked(ev, file.key)"
                      :class="[fileActionButtonClasses, 'focus-visible:outline-indigo-600 bg-indigo-500 hover:bg-indigo-700']"
                    >
                      <FontAwesomeIcon :icon="faCheckCircle" size="lg"/>
                    </button>
                    <button
                      v-else
                      type="button"
                      @click="(ev) => onEnableFileButtonClicked(ev, file.key)"
                      :class="[fileActionButtonClasses, 'focus-visible:outline-indigo-600 bg-indigo-500  hover:bg-indigo-700']"
                    >
                      <FontAwesomeIcon :icon="faCircle" size="lg"/>
                    </button>
                    <button
                      type="button"
                      :key="`delete_${file.key}`"
                      @click="(ev) => onDeleteFileButtonClicked(ev, file.key)"
                      :class="[fileActionButtonClasses, 'bg-red-500 hover:bg-red-700 focus-visible:outline-indigo-600']"
                    >
                      <TrashIcon class="size-5" aria-hidden="true"/>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="5" class="rounded-sm p-2 text-sm">
                  <div class="flex justify-center items-center gap-2 w-full">
                    <button type="button" :class="[fileActionButtonClassesForInfobox, 'focus-visible:outline-indigo-600 bg-indigo-500']">
                      <FontAwesomeIcon :icon="faCheckCircle" size="lg"/>
                    </button>
                    <span v-html="t('settings.fileTable.infobox.agentWillUseFile')"></span>
                    <button type="button" :class="[fileActionButtonClassesForInfobox, 'focus-visible:outline-indigo-600 bg-indigo-500']">
                      <FontAwesomeIcon :icon="faCircle" size="lg"/>
                    </button>
                    <span v-html="t('settings.fileTable.infobox.agentWontUseFile')"></span>
                    <button
                      type="button"
                      :class="[fileActionButtonClassesForInfobox, 'flex justify-center items-center focus-visible:outline-indigo-600 bg-green-600']"
                    >
                      <ArrowDownCircleIcon class="size-5"/>
                    </button>
                    <span>{{ t('settings.fileTable.infobox.downloadFile') }}</span>
                    <button
                      type="button"
                      :class="[fileActionButtonClassesForInfobox, 'flex justify-center items-center bg-red-500 focus-visible:outline-indigo-600']"
                    >
                      <TrashIcon class="size-4" aria-hidden="true"/>
                    </button>
                    <span>{{ t('settings.fileTable.infobox.deleteFile') }}</span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
</template>
